import { useEffect, useState } from 'react';
import { CheckCircle2, CircleAlert, Loader2 } from 'lucide-react';
import { useValidateBulkEpisodeLinks } from '@/hooks/useTVShows';
import { useTranslation } from '@/hooks';
import Tooltip from '@/components/ui/Tooltip';

export interface EpisodeLinkValidationState {
  status: 'checking' | 'valid' | 'invalid';
  issue?: string;
}

interface ValidatedEpisodeLinkInputProps {
  value: string;
  onChange: (value: string) => void;
  validation?: EpisodeLinkValidationState;
  autoValidate?: boolean;
  tvShowId?: string;
  error?: string;
  onValidationChange?: (
    url: string,
    validation: EpisodeLinkValidationState | undefined,
  ) => void;
  className?: string;
}

const baseClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

const ValidatedEpisodeLinkInput = ({
  value,
  onChange,
  validation,
  autoValidate = false,
  tvShowId,
  error,
  onValidationChange,
  className = '',
}: ValidatedEpisodeLinkInputProps) => {
  const { t } = useTranslation();
  const labels = t.bulkEpisodes;
  const { mutateAsync: validateLinks } = useValidateBulkEpisodeLinks();
  const [automaticValidation, setAutomaticValidation] = useState<EpisodeLinkValidationState>();
  const displayedValidation = autoValidate ? automaticValidation : validation;
  const issue = error || displayedValidation?.issue;
  const status = error ? 'invalid' : displayedValidation?.status;

  useEffect(() => {
    if (!autoValidate || !tvShowId) return undefined;

    const url = value.trim();
    if (!url) {
      setAutomaticValidation(undefined);
      onValidationChange?.(value, undefined);
      return undefined;
    }

    const checking: EpisodeLinkValidationState = { status: 'checking' };
    setAutomaticValidation(checking);
    onValidationChange?.(value, checking);

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await validateLinks({ tvShowId, urls: [url] });
        if (cancelled) return;
        const result = results.find((item) => item.url === url);
        const next: EpisodeLinkValidationState = result?.valid
          ? { status: 'valid' }
          : {
              status: 'invalid',
              issue: result?.issue || labels.linkValidationError,
            };
        setAutomaticValidation(next);
        onValidationChange?.(value, next);
      } catch {
        if (cancelled) return;
        const next: EpisodeLinkValidationState = {
          status: 'invalid',
          issue: labels.linkValidationError,
        };
        setAutomaticValidation(next);
        onValidationChange?.(value, next);
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    autoValidate,
    labels.linkValidationError,
    onValidationChange,
    tvShowId,
    validateLinks,
    value,
  ]);

  const validationClass = status === 'valid'
    ? 'border-green-500 pr-9 focus:border-green-500 focus:ring-green-500'
    : status === 'invalid'
      ? 'border-red-500 pr-9 text-red-700 focus:border-red-500 focus:ring-red-500'
      : 'pr-9';

  return (
    <div className="relative">
      <input
        className={`${baseClass} ${validationClass} ${className}`}
        type="url"
        value={value}
        aria-invalid={status === 'invalid'}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="absolute inset-y-0 right-2 flex items-center">
        {status === 'checking' && (
          <Tooltip content={labels.checkingLink}>
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </Tooltip>
        )}
        {status === 'valid' && (
          <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Valid URL" />
        )}
        {status === 'invalid' && (
          <Tooltip content={issue || labels.linkValidationError}>
            <CircleAlert className="h-4 w-4 text-red-500" />
          </Tooltip>
        )}
      </span>
    </div>
  );
};

export default ValidatedEpisodeLinkInput;
